// Acceso, on the browser side: find the person, fetch what we hold, and turn
// it into two files.
//
// TWO FILES ON PURPOSE. The JSON is the portable copy — complete, machine
// readable, what a person would hand to a lawyer or another provider. The text
// summary is the one they will actually read: the recipient is a patient, and
// handing a patient a JSON dump technically answers the request while
// practically refusing it.
//
// The files are built here rather than server-side because that is how this
// codebase already does downloads (Settings.tsx builds its CSV in the browser,
// and no route in the API returns a file). Keeping to that means the API stays
// a JSON API and the formatting stays where the copy lives.

import { apiClient } from '../../api/client.js'

export interface SubjectIdentity {
  channel: string
  externalId: string
  profileName: string | null
}

export interface SubjectCandidate {
  contactId: string
  name: string | null
  email: string | null
  status: string
  lastSeen: string | null
  identities: SubjectIdentity[]
  /** Which field matched, so the searcher can see why this person came back. */
  matchedOn: string[]
}

export interface ContactExport {
  generatedAt: string
  tenantId: string
  contact: Record<string, unknown> | null
  identities: Record<string, unknown>[]
  conversations: Record<string, unknown>[]
  messages: Record<string, unknown>[]
  appointments: Record<string, unknown>[]
  leads: Record<string, unknown>[]
  leadEvents: Record<string, unknown>[]
  customFields: Record<string, unknown>[]
  scheduledEvents: Record<string, unknown>[]
  consentHistory: Record<string, unknown>[]
  counts: Record<string, number>
}

/**
 * GET /admin/tenants/:id/privacy-requests/subjects?q= — finds the data subject
 * behind an ARCO request; `matchedOn` says which field produced each hit.
 */
export async function searchTenantSubjects(
  tenantId: string,
  q: string,
): Promise<SubjectCandidate[]> {
  const res = await apiClient.get<{ data: { subjects: SubjectCandidate[] } }>(
    `/admin/tenants/${tenantId}/privacy-requests/subjects?q=${encodeURIComponent(q)}`,
  )
  return res.data.data.subjects
}

/** Counts of what an erasure touched, table by table. */
export interface EraseResult {
  counts: Record<string, number>
}

/**
 * Irreversible. `confirm` must equal the request's own subjectReference; the
 * server re-checks it inside the transaction that scrubs, so this is a real
 * gate rather than a browser-side courtesy.
 */
export async function eraseTenantRequest(
  tenantId: string,
  id: string,
  confirm: string,
): Promise<EraseResult> {
  const res = await apiClient.post<{ data: EraseResult }>(
    `/admin/tenants/${tenantId}/privacy-requests/${id}/erase`,
    { confirm },
  )
  return res.data.data
}

/**
 * GET /admin/tenants/:t/privacy-requests/:id/export — everything held on the
 * subject as JSON; downloadExport turns it into the two files browser-side.
 */
export async function fetchTenantExport(tenantId: string, id: string): Promise<ContactExport> {
  const res = await apiClient.get<{ data: ContactExport }>(
    `/admin/tenants/${tenantId}/privacy-requests/${id}/export`,
  )
  return res.data.data
}

// ── Building the files ──────────────────────────────────────────────────────

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  messenger: 'Messenger',
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || v === '') return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toLocaleString('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** `1 conversación` / `3 conversaciones`. */
function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * Appointment states, in the words a patient would use.
 *
 * The raw values are English enum keys. Leaving them in meant a legal document
 * addressed to a Mexican patient said "scheduled".
 *
 * These are exactly the five in the appointments_status_check constraint. An
 * unknown value falls through to the raw string rather than being hidden.
 */
const APPOINTMENT_STATUS: Record<string, string> = {
  scheduled: 'agendada',
  confirmed: 'confirmada',
  completed: 'atendida',
  cancelled: 'cancelada',
  no_show: 'no asistió',
}

function str(v: unknown): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  // jsonb columns arrive as objects and arrays. Plain stringification would
  // render them '[object Object]', which in this document means telling
  // someone their own data is [object Object].
  return JSON.stringify(v) ?? ''
}

/**
 * The readable copy.
 *
 * Written in the second person because it is addressed to the patient, not
 * about them. Sections with nothing in them say so rather than being omitted:
 * "no tenemos citas registradas" is itself an answer to what we hold, and a
 * silently missing section reads like something withheld.
 */
export function buildReadableSummary(e: ContactExport): string {
  const L: string[] = []
  const c = e.contact ?? {}
  const name = str(c.full_name) || str(c.display_name) || str(c.profile_name) || 'Sin nombre'

  L.push('DATOS PERSONALES EN NUESTRO SISTEMA')
  L.push('')
  // No trailing period: es-MX renders the time as "05:05 p.m.", so adding one
  // gives "p.m..".
  L.push(`Generado el ${fmtDate(e.generatedAt)}`)
  L.push('')
  L.push('Este documento contiene todo lo que tenemos registrado sobre ti, en respuesta a tu')
  L.push('solicitud de acceso. Se acompaña de un archivo JSON con la misma información en')
  L.push('formato para computadora, por si necesitas trasladarla a otro proveedor.')
  L.push('')

  L.push('── QUIÉN ERES PARA NOSOTROS ─────────────────────────────────')
  L.push(`Nombre: ${name}`)
  if (str(c.email)) L.push(`Correo: ${str(c.email)}`)
  if (str(c.first_seen)) L.push(`Primer contacto: ${fmtDate(c.first_seen)}`)
  if (str(c.last_seen)) L.push(`Último contacto: ${fmtDate(c.last_seen)}`)
  if (str(c.notes)) L.push(`Notas internas: ${str(c.notes)}`)
  if (Array.isArray(c.tags) && c.tags.length > 0) L.push(`Etiquetas: ${c.tags.join(', ')}`)
  L.push('')

  L.push('── CÓMO NOS ESCRIBES ────────────────────────────────────────')
  if (e.identities.length === 0) {
    L.push('No hay canales registrados.')
  } else {
    for (const i of e.identities) {
      const ch = CHANNEL_LABEL[str(i.channel)] ?? str(i.channel)
      L.push(
        `${ch}: ${str(i.external_id)}${str(i.profile_name) ? ` (${str(i.profile_name)})` : ''}`,
      )
    }
  }
  L.push('')

  L.push('── TUS CONVERSACIONES ───────────────────────────────────────')
  L.push(
    e.counts.messages === 0
      ? 'No hay mensajes registrados.'
      : `${plural(e.counts.messages ?? 0, 'mensaje', 'mensajes')} en ` +
          `${plural(e.counts.conversations ?? 0, 'conversación', 'conversaciones')}.`,
  )
  L.push('')
  for (const m of e.messages) {
    const who = str(m.direction) === 'inbound' ? 'Tú' : 'Nosotros'
    L.push(`[${fmtDate(m.created_at)}] ${who}: ${str(m.content)}`)
  }
  L.push('')

  L.push('── TUS CITAS ────────────────────────────────────────────────')
  if (e.appointments.length === 0) {
    L.push('No tenemos citas registradas.')
  } else {
    for (const a of e.appointments) {
      L.push(`${fmtDate(a.starts_at)} — ${APPOINTMENT_STATUS[str(a.status)] ?? str(a.status)}`)
      if (str(a.reason)) L.push(`  Motivo: ${str(a.reason)}`)
      if (str(a.insurer)) L.push(`  Aseguradora: ${str(a.insurer)}`)
      if (str(a.notes)) L.push(`  Notas: ${str(a.notes)}`)
    }
  }
  L.push('')

  if (e.customFields.length > 0) {
    L.push('── OTROS DATOS QUE GUARDAMOS ────────────────────────────────')
    for (const f of e.customFields) {
      L.push(`${str(f.field_key)}: ${JSON.stringify(f.value)}`)
    }
    L.push('')
  }

  if (e.leads.length > 0) {
    L.push('── SEGUIMIENTO COMERCIAL ────────────────────────────────────')
    for (const l of e.leads) {
      L.push(`${str(l.name)} — etapa: ${str(l.stage)}, creado ${fmtDate(l.created_at)}`)
    }
    for (const ev of e.leadEvents) {
      L.push(`  [${fmtDate(ev.created_at)}] ${str(ev.body)}`)
    }
    L.push('')
  }

  L.push('── TU CONSENTIMIENTO ────────────────────────────────────────')
  if (e.consentHistory.length === 0) {
    L.push('No hay registros de consentimiento.')
  } else {
    for (const h of e.consentHistory) {
      L.push(`[${fmtDate(h.occurred_at)}] ${str(h.event)}: ${str(h.new_status)} (${str(h.method)})`)
    }
  }
  L.push('')
  L.push('Si algo aquí es incorrecto, puedes pedir que lo corrijamos escribiendo a')
  L.push('privacidad@inherta.com.')

  return L.join('\n')
}

function download(filename: string, contents: string, mime: string): void {
  // UTF-8 BOM so Windows Notepad and Excel read the accents correctly, the
  // same reason the activity CSV carries one.
  const url = URL.createObjectURL(new Blob([`\uFEFF${contents}`], { type: mime }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** Both files, named after the request so a compliance folder stays tidy. */
export function downloadExport(e: ContactExport, requestRef: string): void {
  const slug = requestRef.replace(/[^\w.-]+/g, '-').slice(0, 40) || 'solicitud'
  const stamp = e.generatedAt.slice(0, 10)
  download(`datos-${slug}-${stamp}.txt`, buildReadableSummary(e), 'text/plain;charset=utf-8')
  download(
    `datos-${slug}-${stamp}.json`,
    JSON.stringify(e, null, 2),
    'application/json;charset=utf-8',
  )
}
